import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { DownloadSimple } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { C, MONO, OUTCOME_COLORS, when, Pill, Eyebrow } from "./hubShared";

const LIMIT = 50;
const OUTCOMES = ["sent", "delivered", "opened", "clicked", "bounced", "failed", "blocked"];
const inp = { padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(26,30,36,0.15)", background: "#fff", fontSize: 12.5 };

export const HubActivity = ({ partner, memberType }) => {
  const [f, setF] = useState({ campaign: "", outcome: "", q: "", date_from: "", date_to: "" });
  const [sq, setSq] = useState("");
  const [data, setData] = useState({ total: 0, items: [], campaigns: [] });
  const [skip, setSkip] = useState(0);
  const req = useRef(0);
  const params = { ...f, q: sq, partner, member_type: memberType };

  const load = useCallback(async (s = 0, query = sq) => {
    const id = ++req.current;
    try {
      const { data: d } = await api.get("/comms-hub/activity", { params: { ...f, q: query, partner, member_type: memberType, skip: s, limit: LIMIT } });
      if (id !== req.current) return;
      setData(d); setSkip(s);
    } catch { if (id === req.current) toast.error("Couldn't load the activity log"); }
  }, [f, sq, partner, memberType]);

  useEffect(() => { load(0); }, [partner, memberType, f.campaign, f.outcome, f.date_from, f.date_to]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = async () => {
    try {
      const r = await api.get("/comms-hub/activity.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "communications-activity.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const set = (k) => (e) => setF((o) => ({ ...o, [k]: e.target.value }));

  return (
    <div data-testid="hub-activity">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select value={f.campaign} onChange={set("campaign")} style={inp} data-testid="hub-activity-campaign">
          <option value="">Every email type</option>
          {data.campaigns.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <select value={f.outcome} onChange={set("outcome")} style={inp} data-testid="hub-activity-outcome">
          <option value="">Any outcome</option>
          {OUTCOMES.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <input type="date" value={f.date_from} onChange={set("date_from")} style={inp} data-testid="hub-activity-from" />
        <input type="date" value={f.date_to} onChange={set("date_to")} style={inp} data-testid="hub-activity-to" />
        <form onSubmit={(e) => { e.preventDefault(); setSq(f.q); load(0, f.q); }}>
          <input value={f.q} onChange={set("q")} placeholder="Name or email, then Enter" style={{ ...inp, width: 200 }} data-testid="hub-activity-search" />
        </form>
        <button onClick={exportCsv} data-testid="hub-activity-export" className="hub-btn" style={{ marginLeft: "auto", background: C.navy, color: "#fff", border: 0 }}>
          <DownloadSimple size={14} /> Export CSV
        </button>
      </div>

      <div style={{ marginTop: 14, background: "#fff", border: "1px solid rgba(26,30,36,0.10)", borderRadius: 10, overflowX: "auto" }}>
        <div style={{ padding: "12px 14px", borderBottom: "1px solid rgba(26,30,36,0.08)" }}>
          <Eyebrow>Every email, newest first · <span data-testid="hub-activity-total">{data.total}</span> found</Eyebrow>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 900 }}>
          <thead>
            <tr style={{ color: C.slate, textAlign: "left" }}>
              {["When", "Member", "Partner", "Email type", "Outcome", "Sent by"].map((h) => <th key={h} style={{ padding: "8px 12px", fontWeight: 600 }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r.id} data-testid={`hub-activity-row-${r.id}`} className="hub-row" style={{ borderTop: "1px solid rgba(26,30,36,0.06)" }}>
                <td style={{ padding: "9px 12px", fontFamily: MONO, fontSize: 11.5, whiteSpace: "nowrap" }}>{when(r.at)}</td>
                <td style={{ padding: "9px 12px" }}>
                  <div style={{ fontWeight: 600, color: C.ink }}>{r.name || "—"}</div>
                  <div style={{ fontSize: 11.5, color: C.slate }}>{r.to}</div>
                </td>
                <td style={{ padding: "9px 12px" }}>{r.partner}{r.member_type === "dependant" ? " · dep." : ""}</td>
                <td style={{ padding: "9px 12px" }}>
                  <div>{r.label}</div>
                  <div style={{ fontSize: 11, color: C.slate, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.subject}</div>
                </td>
                <td style={{ padding: "9px 12px" }}><Pill color={OUTCOME_COLORS[r.outcome]}>{r.outcome}</Pill></td>
                <td style={{ padding: "9px 12px", fontSize: 11.5, color: C.slate }}>{r.sent_by || "—"}</td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={6} style={{ padding: 20, color: C.slate }}>No emails match these filters.</td></tr>}
          </tbody>
        </table>
      </div>

      {data.total > LIMIT && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, fontSize: 12, color: C.slate }}>
          <span data-testid="hub-activity-range">{skip + 1}–{Math.min(skip + LIMIT, data.total)} of {data.total}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button disabled={skip === 0} onClick={() => load(skip - LIMIT)} data-testid="hub-activity-prev" className="hub-btn" style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.15)", color: C.ink }}>Previous</button>
            <button disabled={skip + LIMIT >= data.total} onClick={() => load(skip + LIMIT)} data-testid="hub-activity-next" className="hub-btn" style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.15)", color: C.ink }}>Next</button>
          </div>
        </div>
      )}
    </div>
  );
};
